import React from "react";
import { ResponsivePie } from "@nivo/pie";

import styles from "./CircularProgressBar.module.css";

/**
 * @param {number} percent - the percentage of the progress bar
 * @param {string} color - the color of the progress bar
 * @param {string} size - the size of the progress bar
 * @param {string} percentFontSize - the font size of the percentage
 * @param {string} title - the title of the progress bar
 * @param {string} titleFontSize - the font size of the title
 * @param {number} thickness - the thickness of the progress bar [ 0 - 1 ]
 * @returns {JSX.Element}
 * @description
 * - This component is a circular progress bar that takes in a percentage and displays it in a circular format.
 * - It also takes in a color, size, percentage font size, title, title font size, and thickness.
 * - The component uses the nivo library to create the circular progress bar.
 **/
const CircularProgressBar = ({
  percent,
  color,
  size,
  percentStyles,
  title,
  titleStyles,
  thickness, // 0 to 1
  actAsGauge,
}) => {
  const fakeData = [
    {
      id: "remaining",
      value: 1,
    },
  ];

  const original = [
    {
      id: "consumed",
      value: percent,
    },
    {
      id: "remaining",
      value: 100 - percent,
    },
  ];

  const INNER_RADIUS = 1 - thickness;

  // there are two pie charts, one is the original data and the other is the fake data because we are not able to specify the border radius of the data individually.
  return (
    <div className={styles.progressContainer}>
      <div
        className={styles.fakePieContainer}
        style={{
          width: size,
          height: size,
        }}
      >
        <PieChart
          data={fakeData}
          INNER_RADIUS={INNER_RADIUS}
          actAsGauge={actAsGauge}
        />
        <div className={styles.originalPieContainer}>
          <PieChart
            data={original}
            INNER_RADIUS={INNER_RADIUS}
            color={color}
            actAsGauge={actAsGauge}
          />
          <div className={styles.percentText} style={percentStyles}>
            {`${percent}%`}
          </div>
        </div>
      </div>
      {title && (
        <h2 className={styles.title} style={titleStyles}>
          {title}
        </h2>
      )}
    </div>
  );
};

const PieChart = ({ data, INNER_RADIUS, color, actAsGauge }) => {
  return (
    <ResponsivePie
      data={data}
      innerRadius={INNER_RADIUS}
      cornerRadius={45}
      activeOuterRadiusOffset={9}
      colors={color ? [color, "#DEE6F0"] : "#DEE6F0"}
      tooltip={EmptyToolTip}
      enableRadialLabels={false}
      enableSliceLabels={false}
      enableArcLinkLabels={false}
      enableArcLabels={false}
      animate={true}
      // this is to make the pie chart look like a gauge
      {...(actAsGauge
        ? {
            startAngle: -125,
            endAngle: 125,
            fit: true,
            margin: { top: 0, right: 0, bottom: 0, left: 0 },
          }
        : {})}
    />
  );
};

const EmptyToolTip = () => <></>;

export default CircularProgressBar;
